import { isDefinedError, safe } from "@orpc/client"
import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"
import { ChevronRight, Loader2, Plus, ShieldOff } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { CreateIpBanDialog } from "@/components/ip-bans/create-ip-ban-dialog"
import { IpBanAccounts } from "@/components/ip-bans/ip-ban-accounts"
import { cidrAddress } from "@/components/ip-bans/ip-ban-helpers"
import { PageHeader, Panel } from "@/components/layout"
import { CopyableText } from "@/components/marketplace/copyable-text"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { useDeleteIpBan, useIpBans } from "@/queries/marketplace"
import type { IpBanDto } from "@/types/marketplace.types"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/moderation/ip-bans")({
	component: IpBansPage,
	validateSearch: (search: Record<string, unknown>): { add?: boolean } =>
		search.add === "1" || search.add === true ? { add: true } : {},
})

function formatDate(iso: string): string {
	return format(new Date(iso), "MMM d, yyyy 'at' HH:mm")
}

function IpBanRow({
	ban,
	onLift,
}: {
	ban: IpBanDto
	onLift: (ban: IpBanDto) => void
}) {
	const [expanded, setExpanded] = React.useState(false)
	const accountsId = `ip-ban-accounts-${ban.id}`
	const address = cidrAddress(ban.cidr)

	return (
		<>
			<TableRow className={ban.active ? undefined : "text-muted-foreground"}>
				<TableCell>
					<div className="flex items-center gap-1.5">
						<button
							type="button"
							onClick={() => setExpanded((v) => !v)}
							aria-expanded={expanded}
							aria-controls={accountsId}
							aria-label="Accounts seen on this address"
							title="Accounts seen on this address"
							className="cursor-pointer rounded-sm text-meta hover:text-foreground"
						>
							<ChevronRight
								className={cn(
									"size-4 shrink-0 transition-transform",
									expanded && "rotate-90",
								)}
							/>
						</button>
						<CopyableText text={ban.cidr} />
					</div>
				</TableCell>
				<TableCell className="max-w-md">
					<span className="line-clamp-2 whitespace-normal" title={ban.reason}>
						{ban.reason}
					</span>
				</TableCell>
				<TableCell>
					{ban.createdBy ? (
						<Link
							to="/users/$userId"
							params={{ userId: ban.createdBy.id }}
							className="hover:text-primary-text hover:underline"
						>
							@{ban.createdBy.username}
						</Link>
					) : (
						<span className="text-meta">–</span>
					)}
				</TableCell>
				<TableCell
					className="whitespace-nowrap text-muted-foreground"
					title={ban.createdAt}
				>
					{formatDistanceToNow(new Date(ban.createdAt), { addSuffix: true })}
				</TableCell>
				<TableCell className="whitespace-nowrap text-muted-foreground">
					{ban.expiresAt ? (
						<span title={formatDate(ban.expiresAt)}>
							{ban.active
								? formatDistanceToNow(new Date(ban.expiresAt), {
										addSuffix: true,
									})
								: formatDate(ban.expiresAt)}
						</span>
					) : (
						"Permanent"
					)}
				</TableCell>
				<TableCell>
					{ban.active ? (
						<StatusDot tone="info">Active</StatusDot>
					) : (
						<StatusDot tone="neutral">Expired</StatusDot>
					)}
				</TableCell>
				<TableCell className="text-right">
					<Button variant="ghost" size="sm" onClick={() => onLift(ban)}>
						<ShieldOff />
						Lift ban
					</Button>
				</TableCell>
			</TableRow>
			{expanded && (
				<TableRow id={accountsId} className="hover:bg-transparent">
					<TableCell colSpan={7} className="whitespace-normal py-3 pl-9">
						<div className="flex flex-col gap-1.5">
							<p className="text-xs text-meta">
								Accounts seen on {address}
								{address !== ban.cidr &&
									". Only the range's first address is looked up."}
							</p>
							<IpBanAccounts ip={address} />
						</div>
					</TableCell>
				</TableRow>
			)}
		</>
	)
}

function IpBansPage() {
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canManage = hasRole(AdminRole.MarketplaceUserManagement)
	const bansQuery = useIpBans({ enabled: canManage })
	const deleteBan = useDeleteIpBan()

	const [addOpen, setAddOpen] = React.useState(false)
	const [liftTarget, setLiftTarget] = React.useState<IpBanDto | null>(null)
	const { add } = Route.useSearch()

	React.useEffect(() => {
		if (!add) return
		setAddOpen(true)
		navigate({ to: "/moderation/ip-bans", search: {}, replace: true })
	}, [add, navigate])

	React.useEffect(() => {
		if (!canManage) navigate({ to: "/unauthorized" })
	}, [canManage, navigate])
	if (!canManage) return null

	const bans = bansQuery.data?.bans ?? []
	const activeCount = bans.filter((ban) => ban.active).length

	const handleLift = async () => {
		if (!liftTarget) return
		const target = liftTarget
		const [error] = await safe(deleteBan.mutateAsync({ id: target.id }))
		if (isDefinedError(error)) {
			const e = error as { code: string; message: string }
			toast.error(
				e.code === "IP_BAN_NOT_FOUND"
					? "That ban was already lifted."
					: e.message,
			)
		} else if (error) {
			toast.error(error.message ?? "Failed to lift ban")
		} else {
			toast.success(`Lifted ban on ${target.cidr}`)
		}
		setLiftTarget(null)
	}

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="IP bans"
				count={
					bansQuery.data
						? `${activeCount} active · ${bans.length} total`
						: undefined
				}
				description="Addresses rejected on every API request. A ban takes effect within 30 seconds; the widest range allowed is /16 (IPv4) or /32 (IPv6)."
				actions={
					<Button onClick={() => setAddOpen(true)}>
						<Plus />
						Add ban
					</Button>
				}
			/>

			{bansQuery.error ? (
				<p className="text-bad">Error: {bansQuery.error.message}</p>
			) : bansQuery.isLoading ? (
				<div className="flex flex-col gap-1">
					{Array.from({ length: 6 }, (_, index) => (
						<Skeleton key={index} className="h-9 w-full" />
					))}
				</div>
			) : bans.length === 0 ? (
				<p className="text-muted-foreground">
					No IP bans. Nothing is blocked right now.
				</p>
			) : (
				<Panel>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Address / range</TableHead>
								<TableHead>Reason</TableHead>
								<TableHead>Created by</TableHead>
								<TableHead>Created</TableHead>
								<TableHead>Expires</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{bans.map((ban) => (
								<IpBanRow key={ban.id} ban={ban} onLift={setLiftTarget} />
							))}
						</TableBody>
					</Table>
				</Panel>
			)}

			<CreateIpBanDialog open={addOpen} onOpenChange={setAddOpen} />

			<AlertDialog
				open={liftTarget !== null}
				onOpenChange={(open) => {
					if (!open && !deleteBan.isPending) setLiftTarget(null)
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Lift ban</AlertDialogTitle>
						<AlertDialogDescription>
							Requests from{" "}
							<span className="font-mono font-semibold">
								{liftTarget?.cidr}
							</span>{" "}
							will be accepted again within 30 seconds.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteBan.isPending}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								event.preventDefault()
								handleLift()
							}}
							disabled={deleteBan.isPending}
						>
							{deleteBan.isPending && <Loader2 className="animate-spin" />}
							Lift ban
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
